import { z } from "zod";
import { MechanicalNoteSchema, ProseSchema } from "./prose.js";
/**
 * P4 Assessor output (docs/prompt_pack.md P4). Derived from the OUTPUT JSON
 * plus the six named anchor dimensions. Per-dimension score is 1..5 or null
 * ("If a dimension was not exercised, return null. Do not fabricate a
 * score.") — never a fabricated default.
 *
 * `sample_index` and `model_id` are this task's own addition: P4 is run 3x
 * with dimension order shuffled ("Also required at the engine level"), and a
 * different vendor from the Narrator — both facts this package makes
 * structurally recorded rather than assumed.
 *
 * `process_band` and `engagement_outcome` (inside process_vs_outcome) have no
 * enumerated value set anywhere in prompt_pack.md — the worked example shows
 * only "strong" and "failed" respectively. The three/two-value closed sets
 * below are this package's own reasonable inference; flagged in
 * docs/RECONCILIATION.md rather than left as an unconstrained string.
 */
const ScoreSchema = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]).nullable();
/**
 * WHY A READING CAME OUT BELOW STRONG, in the Assessor's own words, with the
 * learner's words behind it (2026-09-21).
 *
 * A reading used to be a number and up to two quotations: what was said, and
 * never why it read as it did. The coach, asked "why was calibration middling
 * there?", had nothing to cite — and live, as a synthetic learner, it made a
 * cause up ("the habit lapsed"), then retracted it one question later when the
 * quotations showed the habit present. The only honest source for the reason
 * behind a reading is whoever took the reading, so the Assessor now writes it:
 * a short paragraph on what earned the reading and what was missing against
 * the next level, backed by quotations the role verifies are the learner's
 * own words before anything keeps them (roles/assessor.ts).
 *
 * Only for a reading below strong; a strong reading and a dimension that was
 * not exercised carry null. Optional on the stored contract because every
 * record written before it existed has none.
 */
export const READING_REASON_MAX_CHARS = 1_400;
export const READING_REASON_MAX_QUOTES = 4;
export const ReadingReasonSchema = z.strictObject({
    explanation: z.string().min(1).max(READING_REASON_MAX_CHARS),
    /** Verbatim learner words that back the explanation. Verified by code against the utterances the Assessor was shown. */
    quotes: z.array(z.string().min(1)).min(1).max(READING_REASON_MAX_QUOTES),
});
export const AssessmentScoreSchema = ScoreSchema;
export const DimensionScoreSchema = z.strictObject({
    score: ScoreSchema,
    anchor_matched: ScoreSchema,
    /** Up to 2 verbatim learner quotes, per P4's evidence procedure. */
    evidence: z.array(z.string().min(1)).max(2),
    reason: ReadingReasonSchema.nullable().optional(),
});
export const AssessmentSchema = z.strictObject({
    id: z.string().min(1),
    session_id: z.string().min(1),
    turn: z.number().int().min(0),
    sample_index: z.union([z.literal(0), z.literal(1), z.literal(2)]),
    model_id: z.string().min(1),
    scores: z.strictObject({
        structure: DimensionScoreSchema,
        quantitative: DimensionScoreSchema,
        judgment: DimensionScoreSchema,
        communication: DimensionScoreSchema,
        ambiguity_tolerance: DimensionScoreSchema,
        calibration: DimensionScoreSchema,
    }),
    /**
     * What this case was designed to test, in the Assessor's words to the
     * learner (pedagogy review, 2026-09-11; decision 1): one or two sentences
     * that begin "This case was designed to test your …". Optional only because
     * records written before it existed have none.
     */
    case_focus: z.string().min(1).optional(),
    verbosity_bias_check: z.string().min(1),
    decisive_moments: z.array(z.strictObject({ turn: z.number().int().min(0), why_it_mattered: MechanicalNoteSchema })),
    highest_leverage_missed_action: z.strictObject({
        what: MechanicalNoteSchema,
        why: MechanicalNoteSchema,
        when_available: z.string().min(1),
    }),
    process_vs_outcome: z.strictObject({
        process_band: z.enum(["weak", "mixed", "strong"]),
        engagement_outcome: z.enum(["succeeded", "failed", "incomplete"]),
        explanation_for_learner: ProseSchema,
    }),
});
//# sourceMappingURL=assessment.js.map